// pages/ShopDetailsPage.tsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { alertSuccess, alertServerError } from "../../../helpers/alertError";
import PageLayout from "../../../layouts/PageLayout";
import { Store, MapPin, Phone, Clock, Tag, Star, Edit, Trash2, ArrowLeft } from "lucide-react";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import useCustomParams from "../../../hooks/params/useCustomParams";
import { useAuth, useShop } from "@cabukaka/hooks";
import Loader from "../../../components/ui/Loader";

export default function ShopDetailsPage() {
  const { id } = useCustomParams();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const { shop, loading, deleteShop } = useShop({ id: id! })

  const { user } = useAuth()
  const handleDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      deleteShop(id);
      alertSuccess("Boutique supprimée avec succès");
      navigate(-1);
    } catch (error: any) {
      alertServerError(error);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleUpdateShop = () => {
    if (user?.role === "admin") {
      navigate(`/admin/shops/update?id=${id}`)
    } else {
      navigate(`/${user?.role}/update?id=${id}`)
    }
  }

  if (loading) {
    return (
      <PageLayout title="Détails de la boutique">
        <Loader />
      </PageLayout>
    );
  }

  if (!shop) {
    return (
      <PageLayout title="Détails de la boutique">
        <div className="text-center py-20 text-gray-500">Boutique non trouvée</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={shop.name}
      actions={
        <div className="flex gap-2">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
          >
            <ArrowLeft size={18} /> Retour
          </button>
          <button
            onClick={() => {
              handleUpdateShop()
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Edit size={18} /> Modifier
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            <Trash2 size={18} /> Supprimer
          </button>
        </div>
      }
    >
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {/* Image de couverture */}
        {shop.coverImage && (
          <div className="relative h-48 w-full">
            <img
              src={shop.coverImage}
              alt={shop.name}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="p-6">
          <div className="flex items-start gap-6">
            {/* Logo */}
            <div className="w-24 h-24 rounded-xl overflow-hidden bg-gray-100 border-4 border-white shadow-lg flex-shrink-0 -mt-12">
              <img
                src={shop.logo}
                alt={shop.name}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Informations */}
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">{shop.name}</h1>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${shop.open ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                  }`}>
                  {shop.open ? 'Ouvert' : 'Fermé'}
                </div>
              </div>

              <p className="text-gray-600 mt-2">{shop.description}</p>

              <div className="flex flex-wrap gap-4 mt-4 text-sm">
                <div className="flex items-center gap-2 text-gray-500">
                  <MapPin size={16} />
                  {shop.location.city}, {shop.location.address}
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <Phone size={16} />
                  {shop.phone}
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <Clock size={16} />
                  {shop.deliveryTime}
                </div>
                {shop.badge && (
                  <div className="flex items-center gap-2 text-orange-600">
                    <Tag size={16} />
                    {shop.badge}
                  </div>
                )}
                <div className="flex items-center gap-2 text-amber-500">
                  <Star size={16} fill="currentColor" />
                  {shop.rating} ({shop.reviews} avis)
                </div>
              </div>
            </div>
          </div>

          {/* Secteur */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2 text-gray-600">
              <Store size={16} />
              <span>Secteur :</span>
              <span className="font-medium">{shop.sector?.name}</span>
            </div>
          </div>

          {/* Métadonnées */}
          <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-400">
            <p>Créé le : {new Date(shop.createdAt).toLocaleDateString('fr-FR')}</p>
            <p>Dernière modification : {new Date(shop.updatedAt).toLocaleDateString('fr-FR')}</p>
          </div>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Supprimer la boutique"
        message={`Supprimer "${shop.name}" ?`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />
    </PageLayout>
  );
}